import type { ExecutionContext } from '../context/execution-context'
import type { Extension } from '../extension'
import { dynamicParamsLoaderName, type LoaderOwner } from '../loaders/loader-owner'
import { ExtensionMethod } from '../method'
import type { SharedExtensionMetaInfo, SharedExtensionMethod } from '../model/model-types'
import { toArgs, toBodyParams } from '../model/params-model'
import { encodeParams, MethodParams } from '../params'
import { ResultSpec } from '../result'
import type { ActionProps, AnySchema, HandlerBag } from '../types'

// An action — the primary operation a service exposes (07-actions.md). Its `params` are the method input
// itself, keyed by param name, and its handler receives them already parsed.

export class ActionMethod extends ExtensionMethod implements LoaderOwner {
  readonly params: MethodParams
  readonly result: ResultSpec
  readonly dynamicParamsCriteria: MethodParams

  constructor(extension: Extension, private readonly props: ActionProps) {
    super(extension)

    this.params = MethodParams.of(props.params, { serviceId: extension.id, kind: 'action', itemId: props.id, slot: 'params' })
    this.result = ResultSpec.from(props.result)
    this.dynamicParamsCriteria = MethodParams.of(props.dynamicParams?.criteria, {
      serviceId: extension.id,
      kind: 'action',
      itemId: props.id,
      slot: 'dynamicParams.criteria',
    })
  }

  get id(): string {
    return this.props.id
  }

  get name(): string {
    return this.props.id
  }

  get registerAs(): string {
    return 'ACTION'
  }

  get category(): string {
    return this.props.category
  }

  get oauth2Scopes(): string[] {
    return this.props.oauth2Scopes ?? []
  }

  hasDynamicParams(): boolean {
    return !!this.props.dynamicParams
  }

  // ── runtime ────────────────────────────────────────────────────────────────

  /**
   * Normalize, coerce and validate against the declared `params` — the same pipeline every other declared
   * slot uses, so an author expresses optionality in the schema and nowhere else.
   *
   * An action with `dynamicParams` carries the input's undeclared keys through, since its runtime-generated
   * top-level fields are not in the static schema.
   */
  validate(methodData: Record<string, unknown>): Record<string, unknown> {
    return this.params.normalize<Record<string, unknown>>(
      methodData,
      this.input(methodData).invalidInput('params'),
      { carryUnknown: this.hasDynamicParams() }
    )
  }

  execute(params: Record<string, unknown>, ctx: ExecutionContext): unknown {
    return this.props.execute(ctx.bagWith({ params }))
  }

  // Resolve the extra top-level param fields this action generates at runtime (see 03-parameters.md).
  resolveDynamicParams(bag: HandlerBag & { criteria: Record<string, unknown> }): AnySchema | Promise<AnySchema> {
    return this.props.dynamicParams!.resolve(bag)
  }

  // ── output model ───────────────────────────────────────────────────────────

  toModel(): SharedExtensionMethod {
    const [ownStart, ownEnd] = this.props.appearanceColors ?? []
    const [colorStart, colorEnd] = this.extension.appearanceColors
    const metaInfo: SharedExtensionMetaInfo = {
      args: toArgs(this.params),
      ...this.result.toMetaInfo(this.id, this.params),
    }

    if (this.hasDynamicParams()) {
      metaInfo.dynamicParams = {
        methodName: dynamicParamsLoaderName(this.id),
        dependsOn: this.dynamicParamsCriteria.keys(),
      }
    }

    return {
      name: this.id,
      operationName: this.props.label,
      description: this.props.description || '',
      registerAs: this.registerAs,
      category: this.category,
      colorStart: ownStart ?? colorStart,
      colorEnd: ownEnd ?? colorEnd,
      customIcon: this.props.logo ?? '',
      returnType: this.result.returnType,
      queryParams: [],
      bodyParams: toBodyParams(this.params),
      paramsSchema: encodeParams(this.params.scheme),
      metaInfo,
    }
  }
}
